import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Heart, Users, Eye, EyeOff,
  Save, X, MapPin, Zap, Sun, Camera, DollarSign,
  AlertTriangle, CheckCircle, Calendar, Play
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

const SocialProjectsPage = () => {
  const { fetchWithAuth, hasPermission } = useAuth();
  const [projects, setProjects] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [message, setMessage] = useState({ type: '', text: '' });

  const canEdit = hasPermission('settings_edit');

  const defaultFormData = {
    title: '',
    location: '',
    coordinates: '',
    description: '',
    short_description: '',
    story: '',
    image_url: '',
    gallery_images: [],
    video_url: '',
    goal_amount: 0,
    category: 'Educación',
    beneficiaries: 0,
    beneficiaries_description: '',
    community_name: '',
    system_size: '',
    panels_count: 0,
    panel_watts: 450,
    batteries_count: 0,
    inverter_size: '',
    estimated_monthly_generation: 0,
    impact: '',
    co2_offset_yearly: 0,
    hours_of_light_daily: 0,
    devices_powered: '',
    current_situation: '',
    power_outages_monthly: 0,
    has_electricity: true
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [donationForm, setDonationForm] = useState({
    donor_name: '',
    amount: 0,
    message: '',
    is_anonymous: false
  });

  const categories = ['Educación', 'Salud', 'Comunidad', 'Rural'];

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/social-projects');
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error cargando proyectos:', error);
    }
    setLoading(false);
  };

  const loadDonations = async (projectId) => {
    try {
      const res = await fetchWithAuth(`/api/social-projects/${projectId}/donations`);
      if (res.ok) {
        const data = await res.json();
        setDonations(data);
      }
    } catch (error) {
      console.error('Error cargando donaciones:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const url = editingProject 
        ? `/api/social-projects/${editingProject.id}`
        : '/api/social-projects';
      
      const res = await fetchWithAuth(url, {
        method: editingProject ? 'PUT' : 'POST',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: editingProject ? 'Proyecto actualizado' : 'Proyecto creado' });
        setShowModal(false);
        setEditingProject(null);
        setFormData(defaultFormData);
        loadProjects();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Error al guardar' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`¿Eliminar "${project.title}"? Esta acción no se puede deshacer.`)) return;

    try {
      const res = await fetchWithAuth(`/api/social-projects/${project.id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        loadProjects();
      }
    } catch (error) {
      console.error('Error eliminando:', error);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      ...defaultFormData,
      ...project
    });
    setActiveTab('basic');
    setShowModal(true);
  };

  const handleAddDonation = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetchWithAuth(`/api/social-projects/${selectedProject.id}/donations`, {
        method: 'POST',
        body: JSON.stringify({
          ...donationForm,
          project_id: selectedProject.id
        })
      });

      if (res.ok) {
        setShowDonationModal(false);
        setDonationForm({ donor_name: '', amount: 0, message: '', is_anonymous: false });
        loadProjects();
        loadDonations(selectedProject.id);
        setMessage({ type: 'success', text: 'Donación registrada exitosamente' });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const openDonationModal = (project) => {
    setSelectedProject(project);
    loadDonations(project.id);
    setShowDonationModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PY').format(amount);
  };

  const calculateProgress = (raised, goal) => {
    if (!goal) return 0;
    return Math.min((raised / goal) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="social-projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-400" />
            Proyectos de Acción Social
          </h1>
          <p className="text-zinc-400 text-sm">Gestionar proyectos de donación y crowdfunding</p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditingProject(null);
              setFormData(defaultFormData);
              setActiveTab('basic');
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuevo Proyecto
          </button>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
            : 'bg-red-500/10 border border-red-500/30 text-red-400'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Projects Grid */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            {/* Image */}
            <div className="relative h-40">
              <img 
                src={project.image_url || 'https://via.placeholder.com/400x200'} 
                alt={project.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  project.status === 'active' 
                    ? 'bg-emerald-500/80 text-white' 
                    : project.status === 'completed'
                      ? 'bg-blue-500/80 text-white'
                      : 'bg-zinc-500/80 text-white'
                }`}>
                  {project.status === 'active' ? 'Activo' : project.status === 'completed' ? 'Completado' : 'Pausado'}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-center gap-2 text-sm text-zinc-500 mb-2">
                <MapPin className="w-3 h-3" />
                {project.location}
              </div>
              
              <h3 className="font-bold text-white mb-2 line-clamp-2">{project.title}</h3>
              
              {/* Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-emerald-400 font-bold">Gs. {formatCurrency(project.raised_amount)}</span>
                  <span className="text-zinc-500">de Gs. {formatCurrency(project.goal_amount)}</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${calculateProgress(project.raised_amount, project.goal_amount)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-zinc-500 mt-1">
                  <span>{Math.round(calculateProgress(project.raised_amount, project.goal_amount))}%</span>
                  <span>{project.donors_count} donantes</span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 text-sm text-zinc-400 mb-4">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {project.beneficiaries}
                </div>
                <div className="flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  {project.system_size}
                </div>
              </div>

              {/* Actions */}
              {canEdit && (
                <div className="flex gap-2">
                  <button
                    onClick={() => openDonationModal(project)}
                    className="flex-1 py-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
                  >
                    <DollarSign className="w-4 h-4" />
                    Donaciones
                  </button>
                  <button
                    onClick={() => handleEdit(project)}
                    className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(project)}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {projects.length === 0 && (
          <div className="col-span-full text-center py-12">
            <Heart className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No hay proyectos todavía</p>
            <p className="text-zinc-600 text-sm">Crea el primer proyecto de acción social</p>
          </div>
        )}
      </div>

      {/* Project Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 rounded-xl max-w-4xl w-full my-8 border border-zinc-800">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <h3 className="text-lg font-bold text-white">
                {editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-zinc-800 overflow-x-auto">
              {[
                { id: 'basic', label: 'Básico', icon: Edit2 },
                { id: 'media', label: 'Media', icon: Camera },
                { id: 'system', label: 'Sistema Solar', icon: Sun },
                { id: 'impact', label: 'Impacto', icon: Zap },
                { id: 'situation', label: 'Situación', icon: AlertTriangle }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                      activeTab === tab.id 
                        ? 'text-emerald-400 border-b-2 border-emerald-400' 
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <form onSubmit={handleSubmit} className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Basic Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Título *</label>
                      <input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                        placeholder="Luz para Escuela Rural..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Categoría</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Ubicación *</label>
                      <input
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        required
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                        placeholder="Concepción, Paraguay"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Coordenadas (opcional)</label>
                      <input
                        value={formData.coordinates}
                        onChange={(e) => setFormData({ ...formData, coordinates: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                        placeholder="-25.2867, -57.3333"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Nombre de la comunidad</label>
                    <input
                      value={formData.community_name}
                      onChange={(e) => setFormData({ ...formData, community_name: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="Escuela Básica N° 1847..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Descripción corta *</label>
                    <input
                      value={formData.short_description}
                      onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                      required
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="Sistema solar de 3kW para..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Descripción completa</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Historia completa (para la página de detalle)</label>
                    <textarea
                      value={formData.story}
                      onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                      rows={5}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="Cuenta la historia de la comunidad..."
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Meta (Guaraníes) *</label>
                      <input
                        type="number"
                        value={formData.goal_amount}
                        onChange={(e) => setFormData({ ...formData, goal_amount: parseInt(e.target.value) || 0 })}
                        required
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Beneficiarios *</label>
                      <input
                        type="number"
                        value={formData.beneficiaries}
                        onChange={(e) => setFormData({ ...formData, beneficiaries: parseInt(e.target.value) || 0 })}
                        required
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Descripción de beneficiarios</label>
                    <input
                      value={formData.beneficiaries_description}
                      onChange={(e) => setFormData({ ...formData, beneficiaries_description: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="47 niños y 5 profesores"
                    />
                  </div>
                </div>
              )}

              {/* Media Tab */}
              {activeTab === 'media' && (
                <div className="space-y-4">
                  <ImageUploader
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label="Imagen principal *"
                  />

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">URL de Video (YouTube/Vimeo)</label>
                    <input
                      value={formData.video_url}
                      onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="https://www.youtube.com/embed/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Galería de imágenes (URLs)</label>
                    <p className="text-xs text-zinc-500 mb-2">Una URL por línea</p>
                    <textarea
                      value={formData.gallery_images?.join('\n') || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        gallery_images: e.target.value.split('\n').filter(url => url.trim()) 
                      })}
                      rows={4}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                      placeholder="https://ejemplo.com/foto1.jpg&#10;https://ejemplo.com/foto2.jpg"
                    />
                  </div>
                </div>
              )}

              {/* System Tab */}
              {activeTab === 'system' && (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Tamaño del sistema *</label>
                      <input
                        value={formData.system_size}
                        onChange={(e) => setFormData({ ...formData, system_size: e.target.value })}
                        required
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                        placeholder="3 kW"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Cantidad de paneles</label>
                      <input
                        type="number"
                        value={formData.panels_count}
                        onChange={(e) => setFormData({ ...formData, panels_count: parseInt(e.target.value) || 0 })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Watts por panel</label>
                      <input
                        type="number"
                        value={formData.panel_watts}
                        onChange={(e) => setFormData({ ...formData, panel_watts: parseInt(e.target.value) || 0 })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Cantidad de baterías</label>
                      <input
                        type="number"
                        value={formData.batteries_count}
                        onChange={(e) => setFormData({ ...formData, batteries_count: parseInt(e.target.value) || 0 })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Tamaño del inversor</label>
                      <input
                        value={formData.inverter_size}
                        onChange={(e) => setFormData({ ...formData, inverter_size: e.target.value })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                        placeholder="3.5 kW"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Generación mensual estimada (kWh)</label>
                      <input
                        type="number"
                        value={formData.estimated_monthly_generation}
                        onChange={(e) => setFormData({ ...formData, estimated_monthly_generation: parseInt(e.target.value) || 0 })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Impact Tab */}
              {activeTab === 'impact' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Descripción del impacto *</label>
                    <textarea
                      value={formData.impact}
                      onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                      required
                      rows={3}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="Luz para 47 niños, acceso a computadoras..."
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">CO₂ evitado por año (kg)</label>
                      <input
                        type="number"
                        value={formData.co2_offset_yearly}
                        onChange={(e) => setFormData({ ...formData, co2_offset_yearly: parseInt(e.target.value) || 0 })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Horas de luz por día</label>
                      <input
                        type="number"
                        value={formData.hours_of_light_daily}
                        onChange={(e) => setFormData({ ...formData, hours_of_light_daily: parseInt(e.target.value) || 0 })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Equipos a alimentar</label>
                    <input
                      value={formData.devices_powered}
                      onChange={(e) => setFormData({ ...formData, devices_powered: e.target.value })}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="10 computadoras, 1 proyector, 20 luces LED..."
                    />
                  </div>
                </div>
              )}

              {/* Situation Tab */}
              {activeTab === 'situation' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Situación actual</label>
                    <textarea
                      value={formData.current_situation}
                      onChange={(e) => setFormData({ ...formData, current_situation: e.target.value })}
                      rows={4}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      placeholder="Describe la situación actual de la comunidad..."
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-zinc-400 mb-1">Cortes de luz por mes (aprox)</label>
                      <input
                        type="number"
                        value={formData.power_outages_monthly}
                        onChange={(e) => setFormData({ ...formData, power_outages_monthly: parseInt(e.target.value) || 0 })}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                      <input
                        type="checkbox"
                        checked={!formData.has_electricity}
                        onChange={(e) => setFormData({ ...formData, has_electricity: !e.target.checked })}
                        className="w-4 h-4 rounded border-zinc-600"
                      />
                      <label className="text-sm text-zinc-300">No tiene electricidad actualmente</label>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                >
                  <Save className="w-4 h-4" />
                  {editingProject ? 'Guardar Cambios' : 'Crear Proyecto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Donation Modal */}
      {showDonationModal && selectedProject && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-900 rounded-xl max-w-2xl w-full my-8 border border-zinc-800">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div>
                <h3 className="text-lg font-bold text-white">Gestionar Donaciones</h3>
                <p className="text-sm text-zinc-400">{selectedProject.title}</p>
              </div>
              <button onClick={() => setShowDonationModal(false)} className="p-2 hover:bg-zinc-800 rounded-lg">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            <div className="p-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-zinc-800 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-emerald-400">Gs. {formatCurrency(selectedProject.raised_amount)}</p>
                  <p className="text-xs text-zinc-500">Recaudado</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-white">{selectedProject.donors_count}</p>
                  <p className="text-xs text-zinc-500">Donantes</p>
                </div>
                <div className="bg-zinc-800 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-white">{Math.round(calculateProgress(selectedProject.raised_amount, selectedProject.goal_amount))}%</p>
                  <p className="text-xs text-zinc-500">Completado</p>
                </div>
              </div>

              {/* Add Donation Form */}
              <form onSubmit={handleAddDonation} className="bg-zinc-800/50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-white mb-3">Registrar Nueva Donación</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Nombre del donante</label>
                    <input
                      value={donationForm.donor_name}
                      onChange={(e) => setDonationForm({ ...donationForm, donor_name: e.target.value })}
                      required
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-zinc-400 mb-1">Monto (Gs.)</label>
                    <input
                      type="number"
                      value={donationForm.amount}
                      onChange={(e) => setDonationForm({ ...donationForm, amount: parseInt(e.target.value) || 0 })}
                      required
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <label className="flex items-center gap-2 text-sm text-zinc-300">
                    <input
                      type="checkbox"
                      checked={donationForm.is_anonymous}
                      onChange={(e) => setDonationForm({ ...donationForm, is_anonymous: e.target.checked })}
                      className="rounded border-zinc-600"
                    />
                    Donación anónima
                  </label>
                  <button
                    type="submit"
                    className="ml-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm"
                  >
                    Registrar
                  </button>
                </div>
              </form>

              {/* Donations List */}
              <h4 className="font-medium text-white mb-3">Historial de Donaciones</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {donations.length > 0 ? donations.map((donation, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div>
                      <p className="text-white text-sm">{donation.is_anonymous ? 'Anónimo' : donation.donor_name}</p>
                      <p className="text-zinc-500 text-xs">
                        {new Date(donation.created_at).toLocaleDateString('es-PY')}
                      </p>
                    </div>
                    <p className="text-emerald-400 font-bold">Gs. {formatCurrency(donation.amount)}</p>
                  </div>
                )) : (
                  <p className="text-zinc-500 text-center py-4">No hay donaciones registradas</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialProjectsPage;
